import cn from 'classnames';
import Image from 'next/future/image';
import dynamic from 'next/dynamic';
import { useContext, useState } from 'react';
import { CartContext } from 'pages/cart';
import { RC_DURATION, REEBELO_US_SERVICE_PROVIDERS } from '@lambda/reebelo';
import { extractTags } from '@lambda/reebelo/src/tagHelpers';
import { CtaLink } from '@/components/Cta/Cta';
import settings, { priceFormater } from '@/settings';
import NumberInput from '../forms/NumberInput';
import {
  ItemTypeWithProps,
  QtyComponentProps,
  RecommendedItemProps,
  RemoveFromCartArgs,
} from './types';
import {
  handleQtyChange,
  handleAddToCart,
  handleAddToCartExpressShipping,
  handleRemoveFromCart,
} from './recommendedItemsUtils';
import i18n from '@/settings/i18n';
import useCart, { generateCategoryAttr } from '@/lib/use-cart';
import useAttributes from '@/lib/use-attributes';
import infoIconBlack from '@/public/icons/product/infoIconBlack.svg';

const ReebeloCareInfo = ({
  classes,
  onClick,
}: {
  onClick?: () => void;
  classes?: string;
}) => (
  <span
    onClick={(event) => {
      event.stopPropagation();
      if (onClick) onClick();
    }}
    className={cn(
      'relative ml-1 inline-flex h-[12px] w-[12px] min-w-[12px] cursor-pointer items-center self-center',
      classes,
    )}
  >
    <Image
      src={infoIconBlack}
      alt="Reebelo Care"
      className="w-full"
      priority
      loading="eager"
    />
  </span>
);

const WarrantySlideOver = dynamic(
  () => import('@/components/collections/[handle]/WarrantySlideOver'),
  { loading: () => null },
);

const t = i18n.init();

export function RenderQtyComponent({
  update,
  remove,
  product,
  loading,
  itemPrice,
  itemQuantity,
  type,
  selectedMerchId,
  lineItem,
  parentItem,
}: QtyComponentProps) {
  const { setScrollPosOnCartChange } = useContext(CartContext);

  const CartHelpers = useCart();
  const { cartCount: cartSize } = CartHelpers;

  return type !== 'offer' ? (
    <p className="mt-auto flex w-full items-center justify-end text-right">
      <span
        className="mr-1 translate-y-[-1px] cursor-pointer text-left text-xs text-gray-700 underline underline-offset-2 hover:text-blue-500 xxs:mr-2"
        onClick={(e) => {
          e.preventDefault();
        }}
        onClickCapture={handleRemoveFromCart({
          id: selectedMerchId,
          type,
          itemPrice,
          product,
          loading,
          parentItem,
          remove,
          update,
          cartSize,
        } as RemoveFromCartArgs)}
      >
        {t`Remove`}
      </span>
      <span className="text-right text-xs">(x{itemQuantity})</span>
    </p>
  ) : (
    <div
      className={cn(
        'mt-auto flex flex-wrap items-center justify-end text-right',
      )}
    >
      <span
        className="mr-4 cursor-pointer text-right text-xs text-gray-700 underline underline-offset-2 hover:text-blue-500"
        onClick={(e) => {
          e.preventDefault();
        }}
        onClickCapture={handleRemoveFromCart({
          setScrollPosOnCartChange,
          id: selectedMerchId,
          type,
          itemPrice,
          product,
          loading,
          parentItem,
          remove,
          update,
          cartSize,
        })}
      >
        {t`Remove`}
      </span>
      <NumberInput
        min={1}
        value={itemQuantity}
        index={9}
        loading={loading}
        max={product.stock}
        className="mb-1 flex justify-end"
        onChange={handleQtyChange({
          update,
          product,
          price: itemPrice,
          lineId: selectedMerchId,
          availableQty: product.stock,
          lineItem,
        })}
      />
    </div>
  );
}

export function RecommendedItem({
  product,
  index,
  lineItemIndex,
  quantity,
  attributes,
  parentItem,
  type,
  selectedMerchId,
  lineItem,
  onClick,
}: RecommendedItemProps) {
  const [isModalVisible, setModalVisibility] = useState(false);
  const { loading, remove, add, update, setScrollPosOnCartChange } =
    useContext(CartContext);

  const hideModal = () => setModalVisibility(false);
  const isUSStore = settings.store === 'reebelo-us';

  const {
    addCartDateTimeAttribute,
    deliveryAttributes,
    shippingAttributes,
    subsidyAttributes,
    vendorAttributes,
    offerUppricerMetaAttributes,
    categoryPkAttributes,
  } = useAttributes({ product, type } as ItemTypeWithProps);

  const isOffer = type === 'offer';
  const itemQuantity = quantity;
  const itemName = product.title;
  const itemPrice = product.price;
  const itemDuration = type === 'warranty' ? product.duration : 0;
  const itemMonthlyPrice = Number(itemPrice) / Number(itemDuration);
  const itemVendor = product.vendor;
  const itemImage = product.imageSrc;
  const itemPsku = type === 'offer' ? product.psku : '';
  const itemSku = type === 'offer' ? product.sku : '';
  const { variantId } = product;
  const itemSpecs =
    type === 'warranty'
      ? product.specs
      : [
          itemName
            ?.split(' - ')
            .filter(
              (i) => !REEBELO_US_SERVICE_PROVIDERS.includes(i.split('/')[0]),
            )
            .slice(1)
            ?.join(' | '),
        ];
  const itemCondition = type === 'offer' ? product.condition : 'New';
  const itemColor = type === 'offer' ? product.color || 'No Color' : '';

  const getServiceProvider = () => {
    if (type === 'offer') {
      if (product.serviceProviders) {
        try {
          const serviceProviders = Array.isArray(product.serviceProviders)
            ? product.serviceProviders
            : JSON.parse(product.serviceProviders);

          return serviceProviders.includes('Unlocked')
            ? 'Unlocked'
            : serviceProviders.join('/');
        } catch (e) {
          console.error('Error parsing serviceProviders:', e);
        }
      }

      return '';
    }

    return '';
  };
  const itemServiceProvider = getServiceProvider();

  const CartHelpers = useCart();
  const { cartCount: cartSize } = CartHelpers;

  let itemCategory = '';
  let itemStorage = '';

  if (type === 'offer') {
    itemCategory = product.categories ? product.categories.join('|') : '';
    itemStorage = (type === 'offer' && product?.storage) || '';
  }

  const showModal = () => {
    setModalVisibility(true);
  };

  const { category: parentItemCategory = 'default' } = parentItem
    ? {
        ...extractTags(parentItem.merchandise.product.tags),
        ...parentItem.merchandise,
      }
    : {};

  const title = itemName.split(' - ')[0];

  return (
    <div
      className={cn(
        'group mb-3 flex w-full items-start rounded-md border border-gray-200 p-4 last:mb-0',
        {
          'h-full bg-white': isOffer,
          'hover:border-gray-700': isOffer,
          'bg-gradient-to-l from-white to-[#F3FDF8]': !isOffer,
        },
      )}
      id={
        type === 'warranty'
          ? `e2e-reebelo-care-${index}`
          : `e2e-cart-recommended-line-item-${lineItemIndex}-${index}`
      }
    >
      <div
        className={cn(
          'mx-1 flex h-[50px] w-[50px] items-center justify-center',
          {
            'rounded-lg bg-white drop-shadow-lg': !isOffer,
            'rounded-none bg-transparent': isOffer,
            'cursor-pointer ': onClick,
          },
        )}
        onClick={() => {
          if (onClick) onClick('OPEN');
        }}
      >
        <span
          className={cn('h-[50px] w-[50px]', {
            'p-[6px]': type === 'expressShipping',
          })}
        >
          <Image
            width={50}
            height={50}
            alt={itemName}
            src={itemImage as string}
            className="h-full w-[50px] object-contain"
          />
        </span>
      </div>
      <div className="h-full w-full">
        <div className="flex w-full flex-wrap">
          <div
            className={cn(
              'flex h-full w-3/4 flex-col px-2 xs:w-5/6 xs:px-4 md:w-4/5',
              { 'cursor-pointer': onClick },
            )}
            onClick={() => {
              if (onClick) onClick('OPEN');
            }}
          >
            <h2
              className={cn('w-full font-bold !leading-tight', {
                'text-sm': !isOffer,
                'text-xs': isOffer,
              })}
            >
              {title}{' '}
              {isUSStore && itemServiceProvider && `- ${itemServiceProvider}`}
              {type === 'warranty' && (
                <>
                  <span className="hidden sm:inline">
                    {['reebelo-au', 'reebelo-ca'].includes(settings.store)
                      ? ''
                      : 'with ReebeloCare'}
                  </span>
                  <ReebeloCareInfo onClick={showModal} />
                </>
              )}
            </h2>
            {itemSpecs
              .filter((s) => s !== '')
              ?.map((spec: string) => (
                <p
                  key={`${itemName}-${spec}`}
                  className={cn('mt-[3px] w-full text-[13px]', {
                    '!text-xs': type !== 'warranty',
                  })}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: spec.replace('Very Good', 'Good'),
                  }}
                />
              ))}
            {type === 'expressShipping' && (
              <>
                <p className="mt-[3px] w-full text-[13px]">
                  by {product.deliveryDate}{' '}
                  {product.expressDeliveryCutOffFormated != null && (
                    <b>
                      (Order before {product.expressDeliveryCutOffFormated})
                    </b>
                  )}
                </p>
              </>
            )}
          </div>
          <div className="relative flex w-1/4 flex-col flex-wrap justify-start self-stretch pt-0 xs:w-1/6 sm:pt-0 md:w-1/5">
            <p
              className={cn('mb-2 w-full text-right text-sm font-bold', {
                'text-xs': isOffer,
              })}
            >
              {priceFormater.long(itemPrice)}{' '}
              {itemMonthlyPrice && type === 'warranty'
                ? `(${priceFormater.long(itemMonthlyPrice)}/mo)`
                : ''}
            </p>
          </div>
          <div className="flex w-full flex-row justify-end">
            {isOffer && (
              <div
                className="mr-4 flex cursor-pointer flex-col justify-center text-xs text-gray-700 underline underline-offset-2 hover:text-blue-500 xl:hidden xl:group-hover:!flex"
                onClick={() => {
                  if (onClick) onClick('EXPAND');
                }}
              >
                <p>Quick View</p>
              </div>
            )}
            {itemQuantity && itemQuantity > 0 ? (
              <RenderQtyComponent
                {...({
                  update,
                  remove,
                  product,
                  loading,
                  itemPrice,
                  itemQuantity,
                  type,
                  selectedMerchId,
                  lineItem,
                  parentItem,
                } as QtyComponentProps)}
              />
            ) : (
              <div className="mt-auto text-right">
                <CtaLink
                  variant="white"
                  target="_blank"
                  className="!h-auto w-auto rounded-full border border-gray-700 !px-3 !py-2 !text-xs font-bold duration-200 ease-in-out hover:cursor-pointer hover:bg-gray-700 hover:text-white xxs:!py-2 lg:!py-2"
                  onClick={
                    type !== 'expressShipping'
                      ? handleAddToCart({
                          add,
                          loading,
                          variantId,
                          type,
                          title: itemName,
                          price: itemPrice,
                          vendor: itemVendor,
                          itemPsku,
                          itemSku,
                          setScrollPosOnCartChange,
                          attributes:
                            type === 'warranty'
                              ? [
                                  ...attributes,
                                  ...addCartDateTimeAttribute,
                                  {
                                    key: '_offer_category',
                                    value: parentItemCategory,
                                  },
                                  {
                                    key: 'Duration',
                                    value: RC_DURATION['24_MONTH'],
                                  },
                                ]
                              : [
                                  ...vendorAttributes,
                                  ...subsidyAttributes,
                                  ...shippingAttributes,
                                  ...deliveryAttributes,
                                  ...addCartDateTimeAttribute,
                                  ...generateCategoryAttr(
                                    product.productType ||
                                      product.categories?.[0],
                                  ),
                                  ...offerUppricerMetaAttributes,
                                  ...categoryPkAttributes,
                                ],
                          quantity:
                            type === 'warranty' && parentItem
                              ? parentItem.quantity
                              : 1,
                          itemCondition,
                          itemColor,
                          itemStorage,
                          itemCategory,
                          cartSize,
                          itemPosition: index + 1,
                          parentItem,
                        })
                      : handleAddToCartExpressShipping({
                          add,
                          update,
                          loading,
                          variantId,
                          title: itemName,
                          price: itemPrice,
                          parentItem,
                          minDeliveryDate: product.minDeliveryDate,
                          maxDeliveryDate: product.maxDeliveryDate,
                          dispatchMin: product.dispatchMin,
                          dispatchMax: product.dispatchMax,
                          zipcodeStateData: product.zipcodeStateData,
                          cutoffTime: product.expressCutoffTime,
                          cutoffTimezone: product.expressCutoffTimezone,
                          setScrollPosOnCartChange,
                          quantity: parentItem ? parentItem.quantity : 1,
                        })
                  }
                >
                  {t`Add`}
                </CtaLink>
              </div>
            )}
          </div>
        </div>
      </div>
      {type === 'warranty' && (
        <WarrantySlideOver
          price={itemPrice}
          duration={Number(itemDuration)}
          isVisible={isModalVisible}
          hideModal={hideModal}
          warrantyPurchased={itemQuantity > 0}
          purchaseWarranty={handleAddToCart({
            add,
            loading,
            variantId,
            attributes: [
              ...attributes,
              ...addCartDateTimeAttribute,
              { key: 'Duration', value: RC_DURATION['24_MONTH'] },
            ],
            title: itemName,
            price: itemPrice,
            vendor: itemVendor,
            type: 'warranty',
            setScrollPosOnCartChange,
            quantity: parentItem ? parentItem.quantity : 1,
            itemCondition,
            cartSize,
          })}
        />
      )}
    </div>
  );
}
